import React, { useState, useEffect } from 'react';
import { DollarSign, Users, CreditCard, Plus, Edit, Trash2, TrendingUp, Calendar, Calculator, FileText, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { useToast } from '../components/Toast';
import { financialAPI, clientAPI, employeeAPI } from '../services/api';
import StatusBadge from '../components/StatusBadge';

// Interfaces
interface ClientPayment {
  id: string;
  client_id: string;
  appointment_id?: string;
  gross_value: number;
  fees: number;
  taxes: number;
  net_value: number;
  payment_status: 'pending' | 'paid' | 'cancelled';
  payment_date?: string;
  notes?: string;
  created_at: string;
}

interface Cost {
  id: string;
  client_payment_id?: string;
  cost_type: string;
  amount: number;
  description?: string;
}

interface Allocation {
  id: string;
  client_payment_id: string;
  employee_id: string;
  calculation_type: 'fixed' | 'percentage' | 'hourly' | 'hybrid';
  fixed_amount?: number;
  percentage?: number;
  hourly_rate?: number;
  hours_worked?: number;
  calculated_amount: number;
  payment_status: 'pending' | 'paid';
  payment_date?: string;
  notes?: string;
}

interface Employee {
  id: string;
  name: string;
  email: string;
  is_third_party: boolean;
}

interface ManagerBalance {
  month: string;
  total_income: number;
  total_costs: number;
  total_paid_to_contractors: number;
  net_profit: number;
  profit_margin: number;
}

const COST_TYPES = ['phone', 'travel', 'tax', 'fee', 'other'];

const Financeiro: React.FC = () => {
  const { t, formatCurrency, formatDate } = useI18n();
  const { showSuccess, showError, showWarning } = useToast();
  const [activeTab, setActiveTab] = useState<'payments' | 'contractors' | 'allocations' | 'costs' | 'balance'>('payments');
  
  // Payments state
  const [payments, setPayments] = useState<ClientPayment[]>([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState<ClientPayment | null>(null);
  const [paymentForm, setPaymentForm] = useState({
    client_id: '',
    appointment_id: '',
    gross_value: '',
    fees: '0',
    taxes: '0',
    payment_date: '',
    notes: ''
  });

  // Contractors (employees) state
  const [contractors, setContractors] = useState<Employee[]>([]);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);

  // Allocations state
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [selectedPaymentForAllocation, setSelectedPaymentForAllocation] = useState<string | null>(null);
  const [showAllocationForm, setShowAllocationForm] = useState(false);
  const [allocationForm, setAllocationForm] = useState({
    client_payment_id: '',
    employee_id: '',
    calculation_type: 'fixed' as 'fixed' | 'percentage' | 'hourly' | 'hybrid',
    fixed_amount: '',
    percentage: '',
    hourly_rate: '',
    hours_worked: '',
    notes: ''
  });

  // Quick contractor creation state
  const [showQuickContractorForm, setShowQuickContractorForm] = useState(false);
  const [quickContractorForm, setQuickContractorForm] = useState({
    name: '',
    email: '',
    phone: ''
  });

  // Costs state
  const [costs, setCosts] = useState<Cost[]>([]);
  const [showCostForm, setShowCostForm] = useState(false);
  const [editingCost, setEditingCost] = useState<Cost | null>(null);
  const [costForm, setCostForm] = useState({
    client_payment_id: '',
    cost_type: 'phone',
    amount: '',
    description: ''
  });

  // Balance state
  const [balance, setBalance] = useState<ManagerBalance | null>(null);
  const [balanceMonth, setBalanceMonth] = useState(new Date().toISOString().slice(0, 7));

  const [isLoading, setIsLoading] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  
  // Função helper para garantir que sempre seja um array
  const ensureArray = (value: any): any[] => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object' && value.clients && Array.isArray(value.clients)) {
      return value.clients;
    }
    if (value && typeof value === 'object') {
      const values = Object.values(value);
      if (Array.isArray(values) && values.length > 0 && typeof values[0] === 'object') {
        return values;
      }
    }
    return [];
  };

  // Load functions
  const loadPayments = async () => {
    setIsLoading(true);
    try {
      const res = await financialAPI.get('/financial/payments');
      const paymentsData = res.data.payments || res.data || [];
      setPayments(Array.isArray(paymentsData) ? paymentsData : []);
    } catch (error) {
      showError('Erro ao carregar pagamentos');
      setPayments([]); // Garantir array vazio em caso de erro
    }
    setIsLoading(false);
  };

  const loadClients = async () => {
    try {
      const res = await clientAPI.get('/clients');
      
      // Verificar se res.data existe antes de acessar suas propriedades
      if (!res.data) {
        setClients([]);
        return;
      }
      
      let clientsData;
      if (Array.isArray(res.data)) {
        // Se res.data já é um array, use diretamente
        clientsData = res.data;
      } else if (res.data && Array.isArray(res.data.clients)) {
        // Se res.data.clients é um array, use ele
        clientsData = res.data.clients;
      } else if (res.data && res.data.clients && typeof res.data.clients === 'object' && !Array.isArray(res.data.clients)) {
        // Se clients é um objeto, tente converter
        clientsData = Object.values(res.data.clients);
      } else {
        // Fallback para array vazio
        clientsData = [];
      }
      
      const finalClients = ensureArray(clientsData);
      setClients(finalClients);
    } catch (error: any) {
      // Erro ao carregar clientes - continuar sem clientes
      setClients([]); // Garantir que seja sempre um array
    }
  };

  const loadContractors = async () => {
    try {
      // Buscar sem paginação para garantir que pegamos todos os funcionários
      const res = await employeeAPI.get('/employees?page=1&page_size=1000');
      const employeesData = res.data.employees || res.data || [];
      const employees = Array.isArray(employeesData) ? employeesData : [];
      
      // Filtrar terceirizados - verificar tanto is_third_party quanto isThirdParty (camelCase/snake_case)
      const filteredContractors = employees.filter((e: any) => {
        return e.is_third_party === true || e.isThirdParty === true || e.is_third_party === 'true' || e.isThirdParty === 'true';
      });
      
      setAllEmployees(employees);
      setContractors(filteredContractors);
    } catch (error) {
      showError('Erro ao carregar terceirizados');
      setAllEmployees([]);
      setContractors([]);
    }
  };

  const loadAllocations = async (paymentId?: string) => {
    try {
      if (paymentId) {
        const res = await financialAPI.get(`/financial/allocations/payment/${paymentId}`);
        const allocationsData = res.data.allocations || res.data || [];
        setAllocations(Array.isArray(allocationsData) ? allocationsData : []);
      } else {
        setAllocations([]);
      }
    } catch (error) {
      showError('Erro ao carregar alocações');
      setAllocations([]); // Garantir array vazio em caso de erro
    }
  };

  const loadCosts = async () => {
    try {
      const res = await financialAPI.get('/financial/costs');
      const costsData = res.data.costs || res.data || [];
      setCosts(Array.isArray(costsData) ? costsData : []);
    } catch (error) {
      showError('Erro ao carregar custos');
      setCosts([]); // Garantir array vazio em caso de erro
    }
  };

  const loadBalance = async () => {
    try {
      const res = await financialAPI.get(`/financial/balance?month=${balanceMonth}`);
      setBalance(res.data);
    } catch (error) {
      showError('Erro ao carregar saldo');
    }
  };

  useEffect(() => {
    loadClients();
    loadContractors();
    if (activeTab === 'payments') loadPayments();
    if (activeTab === 'costs') loadCosts();
    if (activeTab === 'balance') loadBalance();
  }, [activeTab, balanceMonth]);

  useEffect(() => {
    if (selectedPaymentForAllocation) {
      loadAllocations(selectedPaymentForAllocation);
    }
  }, [selectedPaymentForAllocation]);

  // Helper function para converter ISO 8601 para yyyy-MM-dd (formato do input type="date")
  const formatDateForInput = (dateString: string | undefined | null): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      // Formato yyyy-MM-dd para input type="date"
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      // Erro ao formatar data - usar valor padrão
      return '';
    }
  };

  // Payment handlers
  const createPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const gross = parseFloat(paymentForm.gross_value);
      const fees = parseFloat(paymentForm.fees || '0');
      const taxes = parseFloat(paymentForm.taxes || '0');
      
      // Converter payment_date de "YYYY-MM-DD" para ISO 8601 com hora se fornecido
      let paymentDate: string | null = null;
      if (paymentForm.payment_date && paymentForm.payment_date.trim() !== '') {
        const dateStr = paymentForm.payment_date.trim();
        // Se for apenas data (YYYY-MM-DD), adicionar hora 00:00:00 em UTC
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // Formato apenas data, adicionar hora em ISO 8601
          paymentDate = `${dateStr}T00:00:00Z`;
        } else if (dateStr.includes('T')) {
          // Já está no formato completo com hora
          paymentDate = dateStr;
        } else {
          // Tentar parsear e converter
          try {
            const date = new Date(dateStr);
            if (!isNaN(date.getTime())) {
              paymentDate = date.toISOString();
            }
          } catch (e) {
            // Erro ao converter data - usar valor padrão
          }
        }
      }
      
      const payload: any = {
        client_id: paymentForm.client_id,
        appointment_id: paymentForm.appointment_id || null,
        gross_value: gross,
        fees,
        taxes,
        notes: paymentForm.notes || null
      };
      
      // Só adicionar payment_date se tiver valor válido
      if (paymentDate) {
        payload.payment_date = paymentDate;
      }
      
      await financialAPI.post('/financial/payments', payload);
      
      setShowPaymentForm(false);
      resetPaymentForm();
      await loadPayments();
      showSuccess('Pagamento registrado com sucesso!');
    } catch (error: any) {
      showError(error.response?.data?.error || 'Erro ao criar pagamento');
    }
    setIsLoading(false);
  };

  const updatePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPayment) return;
    setIsLoading(true);
    try {
      // Converter payment_date de "YYYY-MM-DD" para ISO 8601 com hora se fornecido
      let paymentDate: string | null = null;
      if (paymentForm.payment_date && paymentForm.payment_date.trim() !== '') {
        const dateStr = paymentForm.payment_date.trim();
        // Se for apenas data (YYYY-MM-DD), adicionar hora 00:00:00 em UTC
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // Formato apenas data, adicionar hora em ISO 8601
          paymentDate = `${dateStr}T00:00:00Z`;
        } else if (dateStr.includes('T')) {
          // Já está no formato completo com hora
          paymentDate = dateStr;
        } else {
          // Tentar parsear e converter
          try {
            const date = new Date(dateStr);
            if (!isNaN(date.getTime())) {
              paymentDate = date.toISOString();
            }
          } catch (e) {
            // Erro ao converter data - usar valor padrão
          }
        }
      }
      
      const payload: any = {
        gross_value: parseFloat(paymentForm.gross_value),
        fees: parseFloat(paymentForm.fees || '0'),
        taxes: parseFloat(paymentForm.taxes || '0'),
        payment_status: editingPayment.payment_status,
        notes: paymentForm.notes || null
      };
      
      // Só adicionar payment_date se tiver valor válido
      if (paymentDate) {
        payload.payment_date = paymentDate;
      }
      
      await financialAPI.put(`/financial/payments/${editingPayment.id}`, payload);
      
      setShowPaymentForm(false);
      setEditingPayment(null);
      resetPaymentForm();
      await loadPayments();
      showSuccess('Pagamento atualizado!');
    } catch (error: any) {
      showError(error.response?.data?.error || 'Erro ao atualizar pagamento');
    }
    setIsLoading(false);
  };

  const deletePayment = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este pagamento?')) return;
    try {
      await financialAPI.delete(`/financial/payments/${id}`);
      await loadPayments();
      showSuccess('Pagamento excluído!');
    } catch (error: any) {
      showError(error.response?.data?.error || 'Erro ao excluir pagamento');
    }
  };

  const resetPaymentForm = () => {
    setPaymentForm({
      client_id: '',
      appointment_id: '',
      gross_value: '',
      fees: '0',
      taxes: '0',
      payment_date: '',
      notes: ''
    });
  };

  // Allocation handlers
  const createAllocation = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const payload: any = {
        client_payment_id: allocationForm.client_payment_id,
        employee_id: allocationForm.employee_id,
        calculation_type: allocationForm.calculation_type,
        notes: allocationForm.notes || null
      };

      if (allocationForm.calculation_type === 'fixed' && allocationForm.fixed_amount) {
        payload.fixed_amount = parseFloat(allocationForm.fixed_amount);
      } else if (allocationForm.calculation_type === 'percentage' && allocationForm.percentage) {
        payload.percentage = parseFloat(allocationForm.percentage);
      } else if (allocationForm.calculation_type === 'hourly') {
        payload.hourly_rate = parseFloat(allocationForm.hourly_rate);
        payload.hours_worked = parseFloat(allocationForm.hours_worked);
      } else if (allocationForm.calculation_type === 'hybrid') {
        if (allocationForm.fixed_amount) payload.fixed_amount = parseFloat(allocationForm.fixed_amount);
        if (allocationForm.percentage) payload.percentage = parseFloat(allocationForm.percentage);
        if (allocationForm.hourly_rate && allocationForm.hours_worked) {
          payload.hourly_rate = parseFloat(allocationForm.hourly_rate);
          payload.hours_worked = parseFloat(allocationForm.hours_worked);
        }
      }

      await financialAPI.post('/financial/allocations', payload);
      setShowAllocationForm(false);
      resetAllocationForm();
      await loadAllocations(allocationForm.client_payment_id);
      showSuccess('Alocação criada com sucesso!');
    } catch (error: any) {
      showError(error.response?.data?.error || 'Erro ao criar alocação');
    }
    setIsLoading(false);
  };

  const updateAllocationStatus = async (id: string, status: 'pending' | 'paid') => {
    try {
      await financialAPI.put(`/financial/allocations/${id}`, { payment_status: status });
      await loadAllocations(selectedPaymentForAllocation || undefined);
      showSuccess('Status atualizado!');
    } catch (error: any) {
      showError(error.response?.data?.error || 'Erro ao atualizar status');
    }
  };

  const resetAllocationForm = () => {
    setAllocationForm({
      client_payment_id: '',
      employee_id: '',
      calculation_type: 'fixed',
      fixed_amount: '',
      percentage: '',
      hourly_rate: '',
      hours_worked: '',
      notes: ''
    });
  };

  // Quick contractor creation
  const createQuickContractor = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const payload = {
        name: quickContractorForm.name,
        email: quickContractorForm.email,
        phone: quickContractorForm.phone,
        is_third_party: true, // Sempre marcar como terceirizado
        zone: '',
        document: ''
      };

      const res = await employeeAPI.post('/employees', payload);
      
      // Extrair ID do terceirizado criado (a API retorna {id, email, message, ...})
      const newContractorId = res.data.id || res.data.employee?.id || res.data?.id;
      const savedEmail = quickContractorForm.email;
      const savedName = quickContractorForm.name;
      
      // Fechar modal primeiro
      setShowQuickContractorForm(false);
      setQuickContractorForm({ name: '', email: '', phone: '' });
      
      // Recarregar lista de terceirizados de forma mais robusta
      try {
        // Primeiro, buscar diretamente da API para garantir que temos os dados mais recentes (sem paginação)
        const contractorsRes = await employeeAPI.get('/employees?page=1&page_size=1000');
        const allEmployees = contractorsRes.data.employees || contractorsRes.data || [];
        const employeesArray = Array.isArray(allEmployees) ? allEmployees : [];
        // Recarregar lista de funcionários
        
        // Filtrar apenas terceirizados e atualizar o estado - verificar ambos os formatos
        const filteredContractors = employeesArray.filter((e: any) => {
          return e.is_third_party === true || e.isThirdParty === true || e.is_third_party === 'true' || e.isThirdParty === 'true';
        });
        // Atualizar lista de terceirizados
        setAllEmployees(employeesArray);
        setContractors(filteredContractors);
        
        // Aguardar um pouco para garantir que o estado foi atualizado
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Tentar encontrar o terceirizado recém-criado na lista atualizada
        let finalId = newContractorId;
        if (!finalId || !filteredContractors.find((c: Employee) => c.id === finalId)) {
          // Buscar pelo email ou nome se não encontrar pelo ID
          const newContractor = filteredContractors.find((c: Employee) => 
            c.email === savedEmail || 
            c.name === savedName || 
            c.id === newContractorId
          );
          if (newContractor) {
            finalId = newContractor.id;
          }
        }
        
        // Selecionar o terceirizado recém-criado automaticamente no formulário
        if (finalId) {
          setAllocationForm((prev: any) => ({
            ...prev,
            employee_id: finalId
          }));
        }
        
        showSuccess('Terceirizado criado com sucesso! O campo foi atualizado automaticamente.');
      } catch (reloadError) {
        // Mesmo com erro, tentar usar o ID retornado
        if (newContractorId) {
          setAllocationForm((prev: any) => ({
            ...prev,
            employee_id: newContractorId
          }));
        }
        showWarning('Terceirizado criado, mas houve problema ao atualizar a lista. Tente recarregar a página.');
      }
    } catch (error: any) {
      showError(error.response?.data?.error || 'Erro ao criar terceirizado');
    }
    setIsLoading(false);
  };

  // Cost handlers
  const createCost = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await financialAPI.post('/financial/costs', {
        client_payment_id: costForm.client_payment_id || null,
        cost_type: costForm.cost_type,
        amount: parseFloat(costForm.amount),
        description: costForm.description || null
      });
      
      setShowCostForm(false);
      setCostForm({ client_payment_id: '', cost_type: 'phone', amount: '', description: '' });
      await loadCosts();
      showSuccess('Custo registrado!');
    } catch (error: any) {
      showError(error.response?.data?.error || 'Erro ao criar custo');
    }
    setIsLoading(false);
  };

  const deleteCost = async (id: string) => {
    if (!confirm('Excluir este custo?')) return;
    try {
      await financialAPI.delete(`/financial/costs/${id}`);
      await loadCosts();
    } catch (error: any) {
      showError(error.response?.data?.error || 'Erro ao excluir custo');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'cancelled': return '#dc2626';
      default: return '#6c757d';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'paid': return 'Pago';
      case 'pending': return 'Pendente';
      case 'cancelled': return 'Cancelado';
      default: return status;
    }
  };

  const getCostTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      phone: 'Telefone',
      travel: 'Deslocamento',
      tax: 'Imposto',
      fee: 'Taxa',
      other: 'Outro'
    };
    return labels[type] || type;
  };

  const getCalculationTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      fixed: 'Valor Fixo',
      percentage: 'Percentual',
      hourly: 'Por Hora',
      hybrid: 'Híbrido'
    };
    return labels[type] || type;
  };

  return (
    <div style={{ padding: '30px' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: '0 0 10px 0', color: '#333' }}>Gestão Financeira</h1>
        <p style={{ color: '#666', margin: 0 }}>{t('financial.subtitle')}</p>
      </div>

      {/* Tabs */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '10px',
        marginBottom: '20px',
        display: 'flex',
        gap: '10px',
        flexWrap: 'wrap'
      }}>
        {[
          { id: 'payments', label: 'Clientes', icon: CreditCard },
          { id: 'contractors', label: 'Terceirizados', icon: Users },
          { id: 'allocations', label: 'Alocação', icon: Calculator },
          { id: 'costs', label: 'Custos', icon: FileText },
          { id: 'balance', label: 'Saldo do Gestor', icon: TrendingUp }
        ].map(tab => (
          <button
            key={tab.id}
            data-testid={`financeiro-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id as any)}
            style={{
              padding: '10px 20px',
              backgroundColor: activeTab === tab.id ? '#9333ea' : '#f8f9fa',
              color: activeTab === tab.id ? 'white' : '#333',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: activeTab === tab.id ? '600' : '400'
            }}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '20px'
      }}>
        {/* ABA 1: Clientes (Payments) */}
        {activeTab === 'payments' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>Pagamentos dos Clientes</h3>
              <button
                onClick={() => {
                  resetPaymentForm();
                  setEditingPayment(null);
                  // Garantir que clientes estejam carregados antes de abrir o formulário
                  if (!Array.isArray(clients) || clients.length === 0) {
                    loadClients();
                  }
                  setShowPaymentForm(true);
                }}
                style={{
                  backgroundColor: '#9333ea',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Plus size={16} />
                Novo Pagamento
              </button>
            </div>

            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>{t('common.loading')}</div>
            ) : payments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <CreditCard style={{ fontSize: '48px', marginBottom: '10px' }} />
                <div>Nenhum pagamento registrado</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {Array.isArray(payments) && payments.map((payment) => {
                  const client = Array.isArray(clients) ? clients.find(c => c.id === payment.client_id) : undefined;
                  const isSelectedForAllocation = selectedPaymentForAllocation === payment.id;
                  return (
                    <div key={payment.id} style={{
                      border: isSelectedForAllocation ? '3px solid #9333ea' : '1px solid #eee',
                      borderRadius: '8px',
                      padding: '20px',
                      backgroundColor: isSelectedForAllocation ? '#f5f3ff' : '#f9f9f9',
                      position: 'relative',
                      boxShadow: isSelectedForAllocation ? '0 4px 8px rgba(147, 51, 234, 0.2)' : 'none'
                    }}>
                      {isSelectedForAllocation && (
                        <div style={{
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          backgroundColor: '#9333ea',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <Calculator size={12} />
                          Selecionado para Distribuição
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                        <div>
                          <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>
                            {client?.name || `Cliente ${payment.client_id.slice(0, 8)}`}
                          </h4>
                          <div style={{ fontSize: '14px', color: '#666' }}>
                            {payment.payment_date ? formatDate(new Date(payment.payment_date), 'DD/MM/YYYY') : 'Sem data'}
                          </div>
                        </div>
                        <StatusBadge 
                          status={payment.payment_status} 
                          size="small"
                          showIcon={true}
                        />
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '15px' }}>
                        <div>
                          <div style={{ fontSize: '12px', color: '#666' }}>Valor Bruto</div>
                          <div style={{ fontWeight: '600', color: '#333' }}>
                            {formatCurrency(payment.gross_value)}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: '#666' }}>Taxas</div>
                          <div style={{ fontWeight: '600', color: '#333' }}>
                            {formatCurrency(payment.fees)}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: '#666' }}>Impostos</div>
                          <div style={{ fontWeight: '600', color: '#333' }}>
                            {formatCurrency(payment.taxes)}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: '#666' }}>Valor Líquido</div>
                          <div style={{ fontWeight: '600', color: '#10b981', fontSize: '18px' }}>
                            {formatCurrency(payment.net_value)}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '8px', paddingTop: '15px', borderTop: '1px solid #eee', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => {
                            const client = Array.isArray(clients) ? clients.find(c => c.id === payment.client_id) : undefined;
                            // Converter payment_date de ISO 8601 para yyyy-MM-dd para o input type="date"
                            const paymentDateStr = formatDateForInput(payment.payment_date);
                            setPaymentForm({
                              client_id: payment.client_id,
                              appointment_id: payment.appointment_id || '',
                              gross_value: payment.gross_value.toString(),
                              fees: payment.fees.toString(),
                              taxes: payment.taxes.toString(),
                              payment_date: paymentDateStr,
                              notes: payment.notes || ''
                            });
                            setEditingPayment(payment);
                            setShowPaymentForm(true);
                          }}
                          style={{
                            flex: '0 0 auto',
                            minWidth: '100px',
                            padding: '8px 12px',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <Edit size={14} />{t('actions.edit')}</button>
                        <button
                          onClick={() => {
                            if (selectedPaymentForAllocation === payment.id) {
                              // Se já está selecionado, deselecionar
                              setSelectedPaymentForAllocation(null);
                            } else {
                              // Selecionar este pagamento para distribuição
                              setSelectedPaymentForAllocation(payment.id);
                            }
                          }}
                          style={{
                            flex: '0 0 auto',
                            minWidth: '180px',
                            padding: '8px 12px',
                            backgroundColor: isSelectedForAllocation ? '#9333ea' : '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            fontWeight: isSelectedForAllocation ? 'bold' : 'normal',
                            whiteSpace: 'nowrap',
                            boxShadow: isSelectedForAllocation ? '0 2px 4px rgba(147, 51, 234, 0.3)' : 'none'
                          }}
                          title={isSelectedForAllocation ? 'Clique para deselecionar' : 'Selecionar para distribuir'}
                        >
                          <Calculator size={14} />
                          {isSelectedForAllocation ? '✓ Selecionado' : 'Selecionar para Distribuir'}
                        </button>
                        {isSelectedForAllocation && (
                          <button
                            onClick={() => {
                              setActiveTab('allocations');
                            }}
                            style={{
                              flex: '0 0 auto',
                              minWidth: '140px',
                              padding: '8px 12px',
                              backgroundColor: '#f59e0b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              fontWeight: 'bold',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <Calculator size={14} />
                            Ir para Alocação
                          </button>
                        )}
                        <button
                          onClick={() => deletePayment(payment.id)}
                          style={{
                            flex: '0 0 auto',
                            minWidth: '100px',
                            padding: '8px 12px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <Trash2 size={14} />{t('actions.delete')}</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Payment Form Modal */}
            {showPaymentForm && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000
              }}>
                <div style={{
                  backgroundColor: 'white',
                  padding: '30px',
                  borderRadius: '8px',
                  width: '500px',
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  overflowY: 'auto'
                }}>
                  <h3 style={{ margin: '0 0 20px 0' }}>
                    {editingPayment ? 'Editar Pagamento' : 'Novo Pagamento'}
                  </h3>
                  <form onSubmit={editingPayment ? updatePayment : createPayment} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Cliente *</label>
                      <select
                        value={paymentForm.client_id}
                        onChange={(e) => setPaymentForm({...paymentForm, client_id: e.target.value})}
                        required
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                      >
                        <option value="">{t('placeholders.select')}</option>
                        {Array.isArray(clients) && clients.length > 0 ? (
                          clients.map((c: any) => {
                            if (!c || !c.id) return null;
                            return (
                              <option key={c.id} value={c.id}>
                                {c.name || `Cliente ${String(c.id).slice(0, 8)}`}
                              </option>
                            );
                          }).filter(Boolean)
                        ) : (
                          <option disabled>{t('common.loadingClients')}</option>
                        )}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Valor Bruto (R$) *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={paymentForm.gross_value}
                        onChange={(e) => setPaymentForm({...paymentForm, gross_value: e.target.value})}
                        required
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Taxas (R$)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={paymentForm.fees}
                          onChange={(e) => setPaymentForm({...paymentForm, fees: e.target.value})}
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Impostos (R$)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={paymentForm.taxes}
                          onChange={(e) => setPaymentForm({...paymentForm, taxes: e.target.value})}
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Data do Pagamento</label>
                      <input
                        type="date"
                        value={formatDateForInput(paymentForm.payment_date) || paymentForm.payment_date}
                        onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('common.notes')}</label>
                      <textarea
                        value={paymentForm.notes}
                        onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                        rows={3}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </div>
                    {editingPayment && (
                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('common.status')}</label>
                        <select
                          value={editingPayment.payment_status}
                          onChange={(e) => setEditingPayment({...editingPayment, payment_status: e.target.value as any})}
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        >
                          <option value="pending">{t('financial.status.pending')}</option>
                          <option value="paid">{t('financial.status.paid')}</option>
                          <option value="cancelled">{t('financial.status.cancelled')}</option>
                        </select>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      <button
                        type="submit"
                        disabled={isLoading}
                        style={{
                          flex: 1,
                          backgroundColor: editingPayment ? '#10b981' : '#9333ea',
                          color: 'white',
                          border: 'none',
                          padding: '10px',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        {isLoading ? 'Salvando...' : (editingPayment ? 'Atualizar' : 'Criar')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowPaymentForm(false);
                          setEditingPayment(null);
                          resetPaymentForm();
                        }}
                        style={{
                          flex: 1,
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          padding: '10px',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >{t('actions.cancel')}</button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ABA 2: Terceirizados */}
        {activeTab === 'contractors' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>Terceirizados</h3>
            </div>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Gerencie os terceirizados na página de Funcionários. Marque "Terceirizado" ao criar ou editar um funcionário.
            </p>
            {contractors.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <Users style={{ fontSize: '48px', marginBottom: '10px' }} />
                <div>{t('financial.thirdPartyNone')}</div>
                <div style={{ fontSize: '14px', marginTop: '10px' }}>
                  Vá em Funcionários e marque colaboradores como terceirizados
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '15px' }}>
                {Array.isArray(contractors) && contractors.map((contractor) => (
                  <div key={contractor.id} style={{
                    border: '1px solid #eee',
                    borderRadius: '8px',
                    padding: '15px',
                    backgroundColor: '#f9f9f9'
                  }}>
                    <div style={{ fontWeight: '600', color: '#333', marginBottom: '5px' }}>
                      {contractor.name}
                    </div>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                      {contractor.email}
                    </div>
                    <div style={{
                      marginTop: '10px',
                      backgroundColor: '#d1fae5',
                      color: '#065f46',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      display: 'inline-block'
                    }}>
                      Terceirizado
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ABA 3: Alocação */}
        {activeTab === 'allocations' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <h3 style={{ margin: 0 }}>Distribuição para Terceirizados</h3>
                {selectedPaymentForAllocation && (
                  <div style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}>
                    📌 Pagamento selecionado: {(() => {
                      const payment = payments.find(p => p.id === selectedPaymentForAllocation);
                      const client = payment ? (Array.isArray(clients) ? clients.find(c => c.id === payment.client_id) : undefined) : undefined;
                      return payment ? (
                        <span style={{ fontWeight: 'bold', color: '#9333ea' }}>
                          {client?.name || 'Cliente'} - {formatCurrency(payment.net_value)}
                        </span>
                      ) : '';
                    })()}
                    <button
                      onClick={() => setSelectedPaymentForAllocation(null)}
                      style={{
                        marginLeft: '10px',
                        padding: '2px 8px',
                        backgroundColor: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '11px'
                      }}
                      title="Desselecionar pagamento"
                    >
                      ✕ Desmarcar
                    </button>
                  </div>
                )}
              </div>
              {selectedPaymentForAllocation && (
                <button
                  onClick={() => {
                    const payment = payments.find(p => p.id === selectedPaymentForAllocation);
                    if (payment) {
                      setAllocationForm({
                        ...allocationForm,
                        client_payment_id: payment.id
                      });
                      setShowAllocationForm(true);
                    }
                  }}
                  style={{
                    backgroundColor: '#9333ea',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Plus size={16} />
                  Nova Alocação
                </button>
              )}
            </div>

            {!selectedPaymentForAllocation ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <Calculator style={{ fontSize: '48px', marginBottom: '10px', opacity: 0.5 }} />
                <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '10px' }}>Nenhum pagamento selecionado</div>
                <div style={{ marginBottom: '20px', fontSize: '14px' }}>
                  Selecione um pagamento na aba "Clientes" para começar a distribuir
                </div>
                <div style={{ marginTop: '20px' }}>
                  <button
                    onClick={() => setActiveTab('payments')}
                    style={{
                      backgroundColor: '#9333ea',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <CreditCard size={16} />
                    Ir para Pagamentos dos Clientes
                  </button>
                </div>
                <div style={{ marginTop: '30px', padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px', fontSize: '13px', color: '#0369a1', maxWidth: '500px', margin: '20px auto 0' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>💡 Como usar:</div>
                  <ol style={{ textAlign: 'left', margin: '10px 0', paddingLeft: '20px' }}>
                    <li>Vá para a aba "Clientes" (Pagamentos)</li>
                    <li>Encontre o pagamento que deseja distribuir</li>
                    <li>{t('finance.selectToDistribute')}</li>
                    <li>Volte para esta aba ou clique em "Ir para Alocação"</li>
                  </ol>
                </div>
              </div>
            ) : (
              <>
                {allocations.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    <div>Nenhuma alocação para este pagamento</div>
                    <button
                      onClick={() => {
                        const payment = payments.find(p => p.id === selectedPaymentForAllocation);
                        if (payment) {
                          setAllocationForm({
                            ...allocationForm,
                            client_payment_id: payment.id
                          });
                          setShowAllocationForm(true);
                        }
                      }}
                      style={{
                        marginTop: '20px',
                        backgroundColor: '#9333ea',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >{t('finance.createFirstAllocation')}</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {Array.isArray(allocations) && allocations.map((allocation) => {
                      const contractor = contractors.find(c => c.id === allocation.employee_id);
                      const payment = payments.find(p => p.id === allocation.client_payment_id);
                      return (
                        <div key={allocation.id} style={{
                          border: '1px solid #eee',
                          borderRadius: '8px',
                          padding: '20px',
                          backgroundColor: '#f9f9f9'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
                            <div>
                              <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>
                                {contractor?.name || 'Terceirizado'}
                              </h4>
                              <div style={{ fontSize: '14px', color: '#666' }}>
                                Tipo: {getCalculationTypeLabel(allocation.calculation_type)}
                              </div>
                            </div>
                            <StatusBadge 
                              status={allocation.payment_status} 
                              size="small"
                              showIcon={true}
                            />
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '15px' }}>
                            {allocation.fixed_amount && (
                              <div>
                                <div style={{ fontSize: '12px', color: '#666' }}>{t('financial.fixedAmount')}</div>
                                <div style={{ fontWeight: '600' }}>{formatCurrency(allocation.fixed_amount)}</div>
                              </div>
                            )}
                            {allocation.percentage && (
                              <div>
                                <div style={{ fontSize: '12px', color: '#666' }}>{t('financial.percentage')}</div>
                                <div style={{ fontWeight: '600' }}>{allocation.percentage}%</div>
                              </div>
                            )}
                            {allocation.hourly_rate && allocation.hours_worked && (
                              <>
                                <div>
                                  <div style={{ fontSize: '12px', color: '#666' }}>Taxa/Hora</div>
                                  <div style={{ fontWeight: '600' }}>{formatCurrency(allocation.hourly_rate)}</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '12px', color: '#666' }}>Horas</div>
                                  <div style={{ fontWeight: '600' }}>{allocation.hours_worked}h</div>
                                </div>
                              </>
                            )}
                          </div>

                          <div style={{
                            padding: '15px',
                            backgroundColor: '#e0f2fe',
                            borderRadius: '6px',
                            marginBottom: '15px'
                          }}>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Valor Final</div>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#0369a1' }}>
                              {formatCurrency(allocation.calculated_amount)}
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: '8px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
                            {allocation.payment_status === 'pending' && (
                              <button
                                onClick={() => updateAllocationStatus(allocation.id, 'paid')}
                                style={{
                                  flex: 1,
                                  padding: '8px',
                                  backgroundColor: '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '6px'
                                }}
                              >
                                <CheckCircle size={14} />
                                Marcar como Pago
                              </button>
                            )}
                            {allocation.payment_status === 'paid' && (
                              <button
                                onClick={() => updateAllocationStatus(allocation.id, 'pending')}
                                style={{
                                  flex: 1,
                                  padding: '8px',
                                  backgroundColor: '#f59e0b',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '6px'
                                }}
                              >
                                <XCircle size={14} />
                                Reverter para Pendente
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    
                    {payment && (
                      <div style={{
                        padding: '20px',
                        backgroundColor: '#fef3c7',
                        borderRadius: '8px',
                        border: '2px solid #fbbf24'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                              Valor Líquido do Pagamento
                            </div>
                            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#333' }}>
                              {formatCurrency(payment.net_value)}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                              Total Distribuído
                            </div>
                            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#dc2626' }}>
                              {formatCurrency(allocations.reduce((sum, a) => sum + a.calculated_amount, 0))}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                              Lucro do Gestor
                            </div>
                            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#10b981' }}>
                              {formatCurrency(payment.net_value - allocations.reduce((sum, a) => sum + a.calculated_amount, 0))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Allocation Form Modal */}
            {showAllocationForm && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000
              }}>
                <div style={{
                  backgroundColor: 'white',
                  padding: '30px',
                  borderRadius: '8px',
                  width: '600px',
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  overflowY: 'auto'
                }}>
                  <h3 style={{ margin: '0 0 20px 0' }}>{t('financial.newAllocation')}</h3>
                  <form onSubmit={createAllocation} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                        <label style={{ fontWeight: 'bold' }}>Terceirizado *</label>
                        <button
                          type="button"
                          onClick={() => setShowQuickContractorForm(true)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <Plus size={12} />{t('actions.add')}</button>
                      </div>
                      <select
                        value={allocationForm.employee_id}
                        onChange={(e) => setAllocationForm({...allocationForm, employee_id: e.target.value})}
                        required
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                      >
                        <option value="">{t('placeholders.select')}</option>
                        {Array.isArray(contractors) && contractors.length > 0 ? (
                          contractors.map(c => (
                            <option key={c.id} value={c.id}>{c.name || c.email}</option>
                          ))
                        ) : (
                          <option disabled>{t('financial.thirdPartyNone')}</option>
                        )}
                      </select>
                      {Array.isArray(contractors) && contractors.length === 0 && (
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                          Clique em "Adicionar" para criar um terceirizado rapidamente
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Tipo de Cálculo *</label>
                      <select
                        value={allocationForm.calculation_type}
                        onChange={(e) => setAllocationForm({...allocationForm, calculation_type: e.target.value as any})}
                        required
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                      >
                        <option value="fixed">{t('financial.fixedAmount')}</option>
                        <option value="percentage">{t('financial.percentage')}</option>
                        <option value="hourly">Por Hora</option>
                        <option value="hybrid">Híbrido (Fixo + % + Hora)</option>
                      </select>
                    </div>

                    {allocationForm.calculation_type === 'fixed' && (
                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Valor Fixo (R$) *</label>
                        <input
                          type="number"
                          step="0.01"
                          value={allocationForm.fixed_amount}
                          onChange={(e) => setAllocationForm({...allocationForm, fixed_amount: e.target.value})}
                          required
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                      </div>
                    )}

                    {allocationForm.calculation_type === 'percentage' && (
                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Percentual (%) *</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={allocationForm.percentage}
                          onChange={(e) => setAllocationForm({...allocationForm, percentage: e.target.value})}
                          required
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                      </div>
                    )}

                    {allocationForm.calculation_type === 'hourly' && (
                      <>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Valor por Hora (R$) *</label>
                          <input
                            type="number"
                            step="0.01"
                            value={allocationForm.hourly_rate}
                            onChange={(e) => setAllocationForm({...allocationForm, hourly_rate: e.target.value})}
                            required
                            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Horas Trabalhadas *</label>
                          <input
                            type="number"
                            step="0.1"
                            value={allocationForm.hours_worked}
                            onChange={(e) => setAllocationForm({...allocationForm, hours_worked: e.target.value})}
                            required
                            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                          />
                        </div>
                      </>
                    )}

                    {allocationForm.calculation_type === 'hybrid' && (
                      <>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Valor Fixo (R$)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={allocationForm.fixed_amount}
                            onChange={(e) => setAllocationForm({...allocationForm, fixed_amount: e.target.value})}
                            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Percentual (%)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={allocationForm.percentage}
                            onChange={(e) => setAllocationForm({...allocationForm, percentage: e.target.value})}
                            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                          />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Valor por Hora (R$)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={allocationForm.hourly_rate}
                              onChange={(e) => setAllocationForm({...allocationForm, hourly_rate: e.target.value})}
                              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Horas Trabalhadas</label>
                            <input
                              type="number"
                              step="0.1"
                              value={allocationForm.hours_worked}
                              onChange={(e) => setAllocationForm({...allocationForm, hours_worked: e.target.value})}
                              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('common.notes')}</label>
                      <textarea
                        value={allocationForm.notes}
                        onChange={(e) => setAllocationForm({...allocationForm, notes: e.target.value})}
                        rows={3}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </div>

                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      <button
                        type="submit"
                        disabled={isLoading}
                        style={{
                          flex: 1,
                          backgroundColor: '#9333ea',
                          color: 'white',
                          border: 'none',
                          padding: '10px',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        {isLoading ? 'Criando...' : 'Criar Alocação'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAllocationForm(false);
                          resetAllocationForm();
                        }}
                        style={{
                          flex: 1,
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          padding: '10px',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >{t('actions.cancel')}</button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Quick Contractor Form Modal */}
            {showQuickContractorForm && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 3000
              }}>
                <div style={{
                  backgroundColor: 'white',
                  padding: '30px',
                  borderRadius: '8px',
                  width: '450px',
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  overflowY: 'auto'
                }}>
                  <h3 style={{ margin: '0 0 20px 0' }}>{t('finance.addContractorQuick')}</h3>
                  <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
                    Crie um terceirizado rapidamente para continuar com a alocação
                  </p>
                  <form onSubmit={createQuickContractor} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('common.name')}</label>
                      <input
                        type="text"
                        value={quickContractorForm.name}
                        onChange={(e) => setQuickContractorForm({...quickContractorForm, name: e.target.value})}
                        required
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        placeholder="Nome do terceirizado"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Email *</label>
                      <input
                        type="email"
                        value={quickContractorForm.email}
                        onChange={(e) => setQuickContractorForm({...quickContractorForm, email: e.target.value})}
                        required
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        placeholder="email@exemplo.com"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('common.phone')}</label>
                      <input
                        type="tel"
                        value={quickContractorForm.phone}
                        onChange={(e) => setQuickContractorForm({...quickContractorForm, phone: e.target.value})}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        placeholder="(11) 99999-9999"
                      />
                    </div>
                    <div style={{ 
                      padding: '10px', 
                      backgroundColor: '#f0f9ff', 
                      borderRadius: '4px', 
                      fontSize: '12px', 
                      color: '#0369a1' 
                    }}>
                      ℹ️ O terceirizado será criado automaticamente como ativo e será selecionado após a criação.
                    </div>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      <button
                        type="submit"
                        disabled={isLoading}
                        style={{
                          flex: 1,
                          backgroundColor: '#10b981',
                          color: 'white',
                          border: 'none',
                          padding: '10px',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        {isLoading ? 'Criando...' : 'Criar e Continuar'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowQuickContractorForm(false);
                          setQuickContractorForm({ name: '', email: '', phone: '' });
                        }}
                        style={{
                          flex: 1,
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          padding: '10px',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >{t('actions.cancel')}</button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ABA 4: Custos */}
        {activeTab === 'costs' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>{t('financial.costs')}</h3>
              <button
                onClick={() => {
                  setCostForm({ client_payment_id: '', cost_type: 'phone', amount: '', description: '' });
                  setEditingCost(null);
                  setShowCostForm(true);
                }}
                style={{
                  backgroundColor: '#9333ea',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Plus size={16} />
                Novo Custo
              </button>
            </div>

            {costs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <FileText style={{ fontSize: '48px', marginBottom: '10px' }} />
                <div>Nenhum custo registrado</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
                {Array.isArray(costs) && costs.map((cost) => (
                  <div key={cost.id} style={{
                    border: '1px solid #eee',
                    borderRadius: '8px',
                    padding: '15px',
                    backgroundColor: '#f9f9f9'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                      <div>
                        <div style={{ fontWeight: '600', color: '#333', marginBottom: '5px' }}>
                          {getCostTypeLabel(cost.cost_type)}
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#dc2626' }}>
                          {formatCurrency(cost.amount)}
                        </div>
                      </div>
                      <button
                        onClick={() => deleteCost(cost.id)}
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: '#dc2626',
                          cursor: 'pointer',
                          padding: '4px'
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    {cost.description && (
                      <div style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                        {cost.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Cost Form Modal */}
            {showCostForm && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000
              }}>
                <div style={{
                  backgroundColor: 'white',
                  padding: '30px',
                  borderRadius: '8px',
                  width: '400px',
                  maxWidth: '90vw'
                }}>
                  <h3 style={{ margin: '0 0 20px 0' }}>{t('financial.newCost')}</h3>
                  <form onSubmit={createCost} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Tipo *</label>
                      <select
                        value={costForm.cost_type}
                        onChange={(e) => setCostForm({...costForm, cost_type: e.target.value})}
                        required
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                      >
                        {COST_TYPES.map(t => (
                          <option key={t} value={t}>{getCostTypeLabel(t)}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Valor (R$) *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={costForm.amount}
                        onChange={(e) => setCostForm({...costForm, amount: e.target.value})}
                        required
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('common.description')}</label>
                      <textarea
                        value={costForm.description}
                        onChange={(e) => setCostForm({...costForm, description: e.target.value})}
                        rows={3}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      <button
                        type="submit"
                        disabled={isLoading}
                        style={{
                          flex: 1,
                          backgroundColor: '#9333ea',
                          color: 'white',
                          border: 'none',
                          padding: '10px',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        {isLoading ? 'Salvando...' : 'Criar'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowCostForm(false);
                          setCostForm({ client_payment_id: '', cost_type: 'phone', amount: '', description: '' });
                        }}
                        style={{
                          flex: 1,
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          padding: '10px',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >{t('actions.cancel')}</button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ABA 5: Saldo do Gestor */}
        {activeTab === 'balance' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>Saldo do Gestor</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ fontSize: '14px', color: '#666' }}>Mês:</label>
                <input
                  type="month"
                  value={balanceMonth}
                  onChange={(e) => setBalanceMonth(e.target.value)}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
            </div>

            {balance ? (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                  <div style={{
                    backgroundColor: '#d1fae5',
                    borderRadius: '8px',
                    padding: '20px',
                    borderLeft: '4px solid #10b981'
                  }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Entradas</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>
                      {formatCurrency(balance.total_income)}
                    </div>
                  </div>

                  <div style={{
                    backgroundColor: '#fee2e2',
                    borderRadius: '8px',
                    padding: '20px',
                    borderLeft: '4px solid #dc2626'
                  }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>{t('financial.costs')}</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626' }}>
                      {formatCurrency(balance.total_costs)}
                    </div>
                  </div>

                  <div style={{
                    backgroundColor: '#fef3c7',
                    borderRadius: '8px',
                    padding: '20px',
                    borderLeft: '4px solid #f59e0b'
                  }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Pagamentos Terceirizados</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f59e0b' }}>
                      {formatCurrency(balance.total_paid_to_contractors)}
                    </div>
                  </div>

                  <div style={{
                    backgroundColor: '#dbeafe',
                    borderRadius: '8px',
                    padding: '20px',
                    borderLeft: '4px solid #3b82f6'
                  }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Lucro Líquido</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6' }}>
                      {formatCurrency(balance.net_profit)}
                    </div>
                  </div>
                </div>

                <div style={{
                  backgroundColor: '#f5f3ff',
                  borderRadius: '8px',
                  padding: '30px',
                  border: '2px solid #9333ea',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '16px', color: '#666', marginBottom: '10px' }}>Margem de Lucro</div>
                  <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#9333ea' }}>
                    {balance.profit_margin.toFixed(2)}%
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <TrendingUp style={{ fontSize: '48px', marginBottom: '10px' }} />
                <div>{t('finance.loadingMonth')}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Financeiro;
